import React, { createContext, useReducer, ReactElement, useCallback, useContext } from 'react';

type TSelectedAreaOrCompany = {
  id: string;
  name: string;
  coordinates: {
    lat: number;
    lng: number;
  }[];
  timezone: string;
};

type TState = {
  disabled: boolean;
  areaId: TSelectedAreaOrCompany | undefined;
  companyId: TSelectedAreaOrCompany | undefined;
};

enum REDUCER_ACTION_TYPE {
  SET_COMPANY_ID,
  SET_AREA_ID,
  RESET_COMPANY_ID,
  RESET_AREA_ID,
  SET_DISABLED,
}

type TReducerAction =
  | { type: REDUCER_ACTION_TYPE.SET_COMPANY_ID; payload: TSelectedAreaOrCompany }
  | { type: REDUCER_ACTION_TYPE.SET_AREA_ID; payload: TSelectedAreaOrCompany }
  | { type: REDUCER_ACTION_TYPE.RESET_COMPANY_ID }
  | { type: REDUCER_ACTION_TYPE.RESET_AREA_ID }
  | { type: REDUCER_ACTION_TYPE.SET_DISABLED; payload: boolean };

type TTimePlanContext = {
  state: TState;
  setCompanyId: (body: TSelectedAreaOrCompany) => void;
  setAreaId: (body: TSelectedAreaOrCompany) => void;
  resetCompanyId: () => void;
  resetAreaId: () => void;
  setDisabled: (disabled: boolean) => void;
};

type TUseCompaniesAreasContext = ReturnType<typeof useCompaniesAreasContext>;

type TChildren = {
  children?: ReactElement | ReactElement[] | undefined;
};

type TUseCompanyIdHook = {
  companyId: TSelectedAreaOrCompany | undefined;
  disabled: boolean;
  setCompanyId: (body: TSelectedAreaOrCompany) => void;
  resetCompanyId: () => void;
  setDisabled: (disabled: boolean) => void;
};

type TUseAreaIdHook = {
  areaId: TSelectedAreaOrCompany | undefined;
  disabled: boolean;
  setAreaId: (body: TSelectedAreaOrCompany) => void;
  resetAreaId: () => void;
  setDisabled: (disabled: boolean) => void;
};

const getLocalStorageItem = (key: string): TSelectedAreaOrCompany | undefined => {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : undefined;
};

const initState: TState = {
  disabled: false,
  areaId: getLocalStorageItem('areaId'),
  companyId: getLocalStorageItem('companyId'),
};

const reducer = (state: TState, action: TReducerAction): TState => {
  switch (action.type) {
    case REDUCER_ACTION_TYPE.SET_COMPANY_ID:
      localStorage.setItem('companyId', JSON.stringify(action.payload));
      localStorage.removeItem('areaId');
      return { ...state, companyId: action.payload, areaId: undefined };
    case REDUCER_ACTION_TYPE.SET_AREA_ID:
      localStorage.setItem('areaId', JSON.stringify(action.payload));
      return { ...state, areaId: action.payload };
    case REDUCER_ACTION_TYPE.RESET_COMPANY_ID:
      localStorage.removeItem('companyId');
      localStorage.removeItem('areaId');
      return { ...state, companyId: undefined, areaId: undefined };
    case REDUCER_ACTION_TYPE.RESET_AREA_ID:
      localStorage.removeItem('areaId');
      return { ...state, areaId: undefined };
    case REDUCER_ACTION_TYPE.SET_DISABLED:
      return { ...state, disabled: action.payload };
    default:
      throw new Error(`Unknown action type`);
  }
};

const useCompaniesAreasContext = (initState: TState): TTimePlanContext => {
  const [state, dispatch] = useReducer(reducer, initState);
  const setCompanyId = useCallback((body: TSelectedAreaOrCompany) => {
    dispatch({
      type: REDUCER_ACTION_TYPE.SET_COMPANY_ID,
      payload: body,
    });
  }, []);
  const setAreaId = useCallback((body: TSelectedAreaOrCompany) => {
    dispatch({
      type: REDUCER_ACTION_TYPE.SET_AREA_ID,
      payload: body,
    });
  }, []);
  const resetCompanyId = useCallback(() => {
    dispatch({
      type: REDUCER_ACTION_TYPE.RESET_COMPANY_ID,
    });
  }, []);
  const resetAreaId = useCallback(() => {
    dispatch({
      type: REDUCER_ACTION_TYPE.RESET_AREA_ID,
    });
  }, []);
  const setDisabled = useCallback((disabled: boolean) => {
    dispatch({
      type: REDUCER_ACTION_TYPE.SET_DISABLED,
      payload: disabled,
    });
  }, []);

  return {
    state,
    setCompanyId,
    setAreaId,
    resetCompanyId,
    resetAreaId,
    setDisabled,
  };
};

const initContextState: TUseCompaniesAreasContext = {
  state: initState,
  setCompanyId: (_body: TSelectedAreaOrCompany) => {},
  setAreaId: (_body: TSelectedAreaOrCompany) => {},
  resetCompanyId: () => {},
  resetAreaId: () => {},
  setDisabled: (_disabled: boolean) => {},
};

export const CompaniesAreasContext: React.Context<TUseCompaniesAreasContext> =
  createContext<TUseCompaniesAreasContext>(initContextState);

export const CompanyAreaProvider = ({ children }: TChildren): ReactElement => {
  return (
    <CompaniesAreasContext.Provider value={useCompaniesAreasContext(initState)}>
      {children}
    </CompaniesAreasContext.Provider>
  );
};

export const useCompanyId = (): TUseCompanyIdHook => {
  const {
    state: { companyId, disabled },
    setCompanyId,
    resetCompanyId,
    setDisabled,
  } = useContext(CompaniesAreasContext);
  return {
    companyId,
    disabled,
    setCompanyId,
    resetCompanyId,
    setDisabled,
  };
};

export const useAreaId = (): TUseAreaIdHook => {
  const {
    state: { areaId, disabled },
    setAreaId,
    resetAreaId,
    setDisabled,
  } = useContext(CompaniesAreasContext);
  return { areaId, disabled, setAreaId, resetAreaId, setDisabled };
};
export { TSelectedAreaOrCompany };
